<template>
  <div>
    <my-notification :icon="'line-chart'">
      <template #title>
        {{ $t('compCarbonAdminGHGIComparison.title') }}
      </template>
      {{ $t('compCarbonAdminGHGIComparison.info-1') }}
    </my-notification>
    <br />
    <b-notification :closable="false">
      <div class="columns">
        <div class="column">
          <b-field :label="$t('compCarbonAdminGHGIComparison.field-1')">
            <b-taginput
              v-model="GHGIToCompare"
              :data="allGHGI"
              :allow-new="false"
              rounded
              @input="computeGHGI"
              autocomplete
              :open-on-focus="true"
              field="year"
              icon="caret-right"
              :placeholder="$t('compCarbonAdminGHGIComparison.field-2')"
            ></b-taginput>
          </b-field>
        </div>
        <div class="column is-3">
          <b-field :label="$t('compCarbonAdminGHGIComparison.field-3')">
            <b-select expanded @input="computeGHGI" v-model="selectedEFYear">
              <option :value="null">
                {{ $t('compCarbonAdminGHGIComparison.field-4') }}
              </option>
              <option
                :value="year"
                v-for="(year, index) in allEFYears"
                :key="index"
              >
                {{ year }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-2">
          <b-field :label="$t('compCarbonAdminGHGIComparison.field-6')">
            <b-switch v-model="perCapita" />
          </b-field>
        </div>
        <div class="column is-2">
          <b-field :label="$t('compCarbonAdminGHGIComparison.field-5')">
            <b-radio-button
              v-model="displayMode"
              native-value="bar"
              expanded
              type="is-primary"
            >
              <b-icon icon="bar-chart"></b-icon>
            </b-radio-button>
            <b-radio-button v-model="displayMode" expanded native-value="line">
              <b-icon icon="line-chart"></b-icon>
            </b-radio-button>
          </b-field>
        </div>
      </div>
    </b-notification>
    <br />
    <ghgis-bar
      v-show="displayMode === 'bar'"
      :ghgis="sortedGHGIToCompare"
      :modules="modules"
      :colors="colors"
      :perCapita="perCapita"
    />
    <ghgis-line
      v-show="displayMode === 'line'"
      :ghgis="sortedGHGIToCompare"
      :modules="modules"
      :colors="colors"
      :perCapita="perCapita"
    />
  </div>
</template>

<script>
import VEHICLES_FACTORS from '@/../data/factors/carbon/vehiclesFactors.json'
import TRANSPORTS_FACTORS from '@/../data/factors/carbon/transportsFactors.json'
import HEATING_FACTORS from '@/../data/factors/carbon/heatingFactors.json'
import ELECTRICITY_FACTORS from '@/../data/factors/carbon/electricityFactors.json'
import REFRIGERANTS_FACTORS from '@/../data/factors/carbon/refrigerantsFactors.json'
import '@/plugins/highcharts.js'
import { useAdminStore } from '@/stores/admin.js'
import { useCoreStore } from '@/stores/core.js'

export default {
  name: 'GHGIComparison',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'ghgis-bar': () => import('@/components/graphics/GHGIsBar.vue'),
    'ghgis-line': () => import('@/components/graphics/GHGIsLine.vue')
  },
  data () {
    return {
      selectedEFYear: null,
      GHGIToCompare: [],
      displayMode: 'bar',
      perCapita: false
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const coreStore = useCoreStore()
    return { adminStore, coreStore }
  },
  computed: {
    modules: function () {
      return this.coreStore.activeModules(true)
    },
    colors: function () {
      return this.coreStore.modulesColors
    },
    allGHGI: function () {
      return this.adminStore.allGHGI
    },
    sortedGHGIToCompare: function () {
      // don't sort in-place (no side-effect is allowed on data on computed property)
      return this.GHGIToCompare.slice().sort((a, b) =>
        a.year > b.year ? 1 : b.year > a.year ? -1 : 0
      )
    },
    allEFYears: function () {
      let allEFYears = []
      for (let type of Object.keys(VEHICLES_FACTORS)) {
        for (let subtype of Object.keys(VEHICLES_FACTORS[type])) {
          allEFYears = allEFYears.concat(
            Object.keys(VEHICLES_FACTORS[type][subtype].decomposition)
          )
        }
      }
      for (let type of Object.keys(TRANSPORTS_FACTORS)) {
        for (let subtype of Object.keys(TRANSPORTS_FACTORS[type])) {
          allEFYears = allEFYears.concat(
            Object.keys(TRANSPORTS_FACTORS[type][subtype].decomposition)
          )
        }
      }
      for (let type of Object.keys(HEATING_FACTORS)) {
        for (let subtype of Object.keys(HEATING_FACTORS[type])) {
          allEFYears = allEFYears.concat(
            Object.keys(HEATING_FACTORS[type][subtype].decomposition)
          )
        }
      }
      for (let type of Object.keys(ELECTRICITY_FACTORS)) {
        allEFYears = allEFYears.concat(
          Object.keys(ELECTRICITY_FACTORS[type].decomposition)
        )
      }
      for (let type of Object.keys(REFRIGERANTS_FACTORS)) {
        allEFYears = allEFYears.concat(
          Object.keys(REFRIGERANTS_FACTORS[type].decomposition)
        )
      }
      return Array.from(new Set(allEFYears)).sort(
        (a, b) => parseInt(b) - parseInt(a)
      )
    }
  },
  methods: {
    computeGHGI: function () {
      this.adminStore.computeAllGHGI(this.selectedEFYear)
    },
    initiGHGIToCompare: function () {
      this.GHGIToCompare = this.allGHGI
    }
  },
  watch: {
    GHGIToCompare: function () {
      this.computeGHGI()
    },
    allGHGI: function () {
      this.initiGHGIToCompare()
    }
  },
  mounted () {
    // make sure we populate the store with all the GHGI and their consumption
    // NOTE(msimonin): as an improvement we could fetch only the missing consumptions
    const loadingComponent = this.$buefy.loading.open({
      container: null
    })
    this.adminStore.getAllGHGI().then((data) => {
      this.initiGHGIToCompare()
      loadingComponent.close()
    })
  }
}
</script>

<style>
.is-sticky {
  background: #fafafa !important;
}
.table td {
  vertical-align: middle !important;
}
.table th {
  vertical-align: middle !important;
}
</style>
